import {
  BaseContractName,
  EthereumContractName,
  ExternalBaseContractName,
} from '@repo/types-client';

import {
  AvatarABI,
  AvatarMetadataABI,
  CoreMinterABI,
  delegatecashABI,
  doodlesABI,
  DoodlesAccessManagerABI,
  DoodlesCertifiedViralABI,
  DoodlesDropsABI,
  dooplicationABI,
  dooplicationMarketplaceABI,
  dooplicatorABI,
  ERC2771ForwarderABI,
  ExecutionAuthorityABI,
  genesisBoxABI,
  genesisOpenerABI,
  PackABI,
  PassABI,
  ReedemableABI,
  spaceDoodlesABI,
  SplitterManagerABI,
  TemplateRegistryABI,
  WearableABI,
} from '../abi';
import {
  baseContractAddresses,
  ethereumContractAddresses,
  externalBaseContractAddresses,
} from '../data';

/**
 * Interface defining the structure of contract mappings across different networks.
 * Each network contains a record of contracts with their addresses and ABIs.
 */
interface ContractMapping {
  /** Ethereum mainnet contract mappings */
  ethereum: Record<
    Capitalize<EthereumContractName>,
    {
      address: string;
      abi: object[];
    }
  >;
  /** Base network contract mappings */
  base: Record<
    Capitalize<BaseContractName>,
    {
      address: string;
      abi: object[];
    }
  >;
  /** External Base network contract mappings */
  'external-base': Record<
    Capitalize<ExternalBaseContractName>,
    {
      address: string;
      abi: object[];
    }
  >;
}

/**
 * Global mapping of all smart contracts across different networks.
 * Contains contract addresses and their corresponding ABIs.
 *
 * @remarks
 * - ethereum: Contains Doodles mainnet contracts
 * - base: Contains core protocol contracts on Base
 * - external-base: Contains third-party or auxiliary contracts on Base
 */
export const contractMapping: ContractMapping = {
  ethereum: {
    // Ethereum Mainnet NFT Collections and Core Contracts
    Doodles: {
      address: ethereumContractAddresses.doodles,
      abi: doodlesABI,
    },
    DoodlesDrops: {
      address: ethereumContractAddresses.doodlesDrops,
      abi: DoodlesDropsABI,
    },
    Dooplicator: {
      address: ethereumContractAddresses.dooplicator,
      abi: dooplicatorABI,
    },
    Dooplication: {
      address: ethereumContractAddresses.dooplication,
      abi: dooplicationABI,
    },
    DooplicationMarketplace: {
      address: ethereumContractAddresses.dooplicationMarketplace,
      abi: dooplicationMarketplaceABI,
    },
    DelegateCash: {
      address: ethereumContractAddresses.delegateCash,
      abi: delegatecashABI,
    },
    GenesisBox: {
      address: ethereumContractAddresses.genesisBox,
      abi: genesisBoxABI,
    },
    GenesisOpener: {
      address: ethereumContractAddresses.genesisOpener,
      abi: genesisOpenerABI,
    },
    SpaceDoodles: {
      address: ethereumContractAddresses.spaceDoodles,
      abi: spaceDoodlesABI,
    },
  },
  base: {
    // Base Network Core Protocol Contracts
    Avatar: {
      address: baseContractAddresses.avatar,
      abi: AvatarABI,
    },
    AvatarMetadata: {
      address: baseContractAddresses.avatarMetadata,
      abi: AvatarMetadataABI,
    },
    CoreMinter: {
      address: baseContractAddresses.coreMinter,
      abi: CoreMinterABI,
    },
    DoodlesAccessManager: {
      address: baseContractAddresses.doodlesAccessManager,
      abi: DoodlesAccessManagerABI,
    },
    Erc2771Forwarder: {
      address: baseContractAddresses.erc2771Forwarder,
      abi: ERC2771ForwarderABI,
    },
    ExecutionAuthority: {
      address: baseContractAddresses.executionAuthority,
      abi: ExecutionAuthorityABI,
    },
    Wearable: {
      address: baseContractAddresses.wearable,
      abi: WearableABI,
    },
    Pack: {
      address: baseContractAddresses.pack,
      abi: PackABI,
    },
    Pass: {
      address: baseContractAddresses.pass,
      abi: PassABI,
    },
    Redeemable: {
      address: baseContractAddresses.redeemable,
      abi: ReedemableABI,
    },
    SplitterManager: {
      address: baseContractAddresses.splitterManager,
      abi: SplitterManagerABI,
    },
    TemplateRegistry: {
      address: baseContractAddresses.templateRegistry,
      abi: TemplateRegistryABI,
    },
  },
  'external-base': {
    // External Contracts and Integrations on Base
    DoodlesCertifiedViral: {
      address: externalBaseContractAddresses.doodlesCertifiedViral,
      abi: DoodlesCertifiedViralABI,
    },
    DoodlesComics: {
      address: externalBaseContractAddresses.doodlesComics,
      abi: DoodlesCertifiedViralABI,
    },
    UtbExecutor: {
      address: externalBaseContractAddresses.utbExecutor,
      abi: [],
    },
  },
};
